import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BookingFormModal from "../../components/modals/BookingFormModal";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import { RouteNames } from "../../navigation/RouteNames";
import { CreateBookingRequest } from "../../models/RequestModels/CreateBookingRequest";
import { UpdateBookingRequest } from "../../models/RequestModels/UpdateBookingRequest";
import {
  BookingResponse,
  BookingSource,
} from "../../models/ResponseModels/BookingResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { createBooking, fetchBookings } from "../../store/slices/bookingSlice";
import { fetchUnits } from "../../store/slices/unitSlice";
import { useTranslation } from "../../translations/i18n";
import { formatMoney } from "../../utils/helperFunctions/currencyFormatter";
import { formatLongDisplayDate } from "../../utils/helperFunctions/dateFormatter";

type ReservationTab = "active" | "completed" | "cancelled";

const getBookingSource = (booking: BookingResponse): BookingSource => {
  return booking.source || "manual";
};

const isActiveBooking = (booking: BookingResponse) => {
  return ["reserved", "checked_in"].includes(booking.status);
};

const ReservationsScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { bookings, loading: bookingsLoading } = useAppSelector(
    (state) => state.bookings,
  );
  const { units, loading: unitsLoading } = useAppSelector(
    (state) => state.units,
  );
  const { account } = useAppSelector((state) => state.auth);

  const [tab, setTab] = useState<ReservationTab>("active");
  const [source, setSource] = useState<BookingSource | "all">("all");
  const [bookingFormOpen, setBookingFormOpen] = useState(false);

  const loading = bookingsLoading || unitsLoading;
  const defaultCurrency = account?.default_currency || "BAM";

  useEffect(() => {
    dispatch(fetchUnits());
    dispatch(fetchBookings(undefined));
  }, [dispatch]);

  const sourceFilteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      return source === "all" || getBookingSource(booking) === source;
    });
  }, [bookings, source]);

  const stats = useMemo(() => {
    const active = sourceFilteredBookings.filter(isActiveBooking).length;

    const completed = sourceFilteredBookings.filter(
      (booking) => booking.status === "checked_out",
    ).length;

    const cancelled = sourceFilteredBookings.filter(
      (booking) => booking.status === "cancelled",
    ).length;

    const totalRevenue = sourceFilteredBookings
      .filter((booking) => booking.status !== "cancelled")
      .reduce((sum, booking) => sum + Number(booking.total_price || 0), 0);

    return {
      active,
      completed,
      cancelled,
      totalRevenue,
    };
  }, [sourceFilteredBookings]);

  const filteredBookings = useMemo(() => {
    return sourceFilteredBookings.filter((booking) => {
      if (tab === "active") {
        return isActiveBooking(booking);
      }

      if (tab === "completed") {
        return booking.status === "checked_out";
      }

      return booking.status === "cancelled";
    });
  }, [sourceFilteredBookings, tab]);

  const grouped = useMemo(() => {
    return units
      .map((unit) => ({
        unit,
        bookings: filteredBookings
          .filter((booking) => booking.unit_id === unit.id)
          .sort((first, second) =>
            String(first.check_in).localeCompare(String(second.check_in)),
          ),
      }))
      .filter((group) => group.bookings.length > 0)
      .sort((first, second) => first.unit.name.localeCompare(second.unit.name));
  }, [filteredBookings, units]);

  const handleRefresh = () => {
    dispatch(fetchBookings(undefined));
  };

  const handleCreateBooking = async (
    body: CreateBookingRequest | UpdateBookingRequest,
  ) => {
    const result = await dispatch(
      createBooking({
        ...(body as CreateBookingRequest),
        source: "manual",
      }),
    );

    if (createBooking.fulfilled.match(result)) {
      setBookingFormOpen(false);
      dispatch(fetchBookings(undefined));
    }
  };

  return (
    <Box>
      <AppLoadingOverlay visible={loading} />

      <PageHeader
        title={t("reservations.title")}
        description={t("reservations.subtitle")}
        action={
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              rowGap: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              {t("common.refresh")}
            </Button>

            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              onClick={() => navigate(RouteNames.calendar)}
            >
              {t("tabs.calendar")}
            </Button>

            <Button
              variant="contained"
              onClick={() => setBookingFormOpen(true)}
              sx={{ borderRadius: 999, fontWeight: 900 }}
            >
              {t("reservations.newReservation")}
            </Button>
          </Stack>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard label={t("reservations.active")} value={stats.active} />
        <StatCard label={t("reservations.completed")} value={stats.completed} />
        <StatCard label={t("reservations.cancelled")} value={stats.cancelled} />
        <StatCard
          label={t("reservations.totalRevenue")}
          value={formatMoney(stats.totalRevenue, defaultCurrency)}
        />
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 4,
          border: `1px solid ${AppColors.border}`,
          boxShadow: AppColors.shadowCard,
        }}
      >
        <CardContent
          sx={{
            p: { xs: 2, sm: 2.5 },
            "&:last-child": {
              pb: { xs: 2, sm: 2.5 },
            },
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            sx={{
              justifyContent: "space-between",
              alignItems: { xs: "stretch", lg: "center" },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <FilterChip
                active={tab === "active"}
                label={`${t("reservations.active")} (${stats.active})`}
                onClick={() => setTab("active")}
              />

              <FilterChip
                active={tab === "completed"}
                label={`${t("reservations.completed")} (${stats.completed})`}
                onClick={() => setTab("completed")}
              />

              <FilterChip
                active={tab === "cancelled"}
                label={`${t("reservations.cancelled")} (${stats.cancelled})`}
                onClick={() => setTab("cancelled")}
              />
            </Stack>

            <TextField
              select
              size="small"
              label={t("reservations.source")}
              value={source}
              onChange={(event) =>
                setSource(event.target.value as BookingSource | "all")
              }
              sx={{
                minWidth: { xs: "100%", lg: 240 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            >
              <MenuItem value="all">{t("reservations.allSources")}</MenuItem>
              <MenuItem value="manual">{t("bookingSources.manual")}</MenuItem>
              <MenuItem value="airbnb">{t("bookingSources.airbnb")}</MenuItem>
              <MenuItem value="booking">{t("bookingSources.booking")}</MenuItem>
              <MenuItem value="external">
                {t("bookingSources.external")}
              </MenuItem>
              <MenuItem value="other">{t("bookingSources.other")}</MenuItem>
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {!loading && grouped.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${AppColors.border}`,
            boxShadow: AppColors.shadowCard,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
              {t("reservations.title")}
            </Typography>

            <Typography color="text.secondary">
              {t("reservations.noReservationsForUnit")}
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {!loading && grouped.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {grouped.map(({ unit, bookings: unitBookings }, index) => (
            <UnitReservationCard
              key={unit.id}
              unit={unit}
              bookings={unitBookings}
              currency={defaultCurrency}
              accent={getUnitAccent(index)}
            />
          ))}
        </Box>
      ) : null}

      <BookingFormModal
        open={bookingFormOpen}
        loading={loading}
        units={units}
        defaultCurrency={defaultCurrency}
        onClose={() => setBookingFormOpen(false)}
        onSubmit={handleCreateBooking}
      />
    </Box>
  );
};

const UnitReservationCard = ({
  unit,
  bookings,
  currency,
  accent,
}: {
  unit: UnitResponse;
  bookings: BookingResponse[];
  currency: string;
  accent: string;
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box
        sx={{
          borderTop: `4px solid ${accent}`,
          px: 2.5,
          py: 2,
          bgcolor: AppColors.card,
          borderBottom: `1px solid ${AppColors.borderSoft}`,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "999px",
                  bgcolor: accent,
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  fontWeight: 950,
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {unit.name}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, ml: 2.1 }}
            >
              {unit.property?.name || ""}
            </Typography>
          </Box>

          <Chip
            size="small"
            label={`${bookings.length} ${t("reservations.reservations")}`}
            sx={{
              fontWeight: 900,
              bgcolor: AppColors.surfaceSoft,
              border: `1px solid ${AppColors.border}`,
            }}
          />
        </Stack>
      </Box>

      <CardContent
        sx={{
          p: 2,
          "&:last-child": {
            pb: 2,
          },
        }}
      >
        <Stack spacing={1.25}>
          {bookings.map((booking) => (
            <ReservationRow
              key={booking.id}
              booking={booking}
              currency={currency}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ReservationRow = ({
  booking,
  currency,
}: {
  booking: BookingResponse;
  currency: string;
}) => {
  const { t } = useTranslation();
  const source = getBookingSource(booking);

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 3,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.surface,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {booking.guest?.full_name || t("reservations.unknownGuest")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.35, fontWeight: 700 }}
          >
            {formatLongDisplayDate(booking.check_in)} →{" "}
            {formatLongDisplayDate(booking.check_out)} · {booking.nights}{" "}
            {t("reservations.nights")}
            {booking.guest_count ? ` · ${booking.guest_count}` : ""}
          </Typography>

          {booking.notes ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.85,
                color: AppColors.textSecondary,
                wordBreak: "break-word",
              }}
            >
              {booking.notes}
            </Typography>
          ) : null}
        </Box>

        <Stack
          spacing={1}
          sx={{
            alignItems: { xs: "flex-start", md: "flex-end" },
            minWidth: { xs: "auto", md: 180 },
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              rowGap: 1,
            }}
          >
            <SourceChip source={source} />
            <PaymentChip paymentStatus={booking.payment_status} />
          </Stack>

          <Typography
            sx={{
              fontWeight: 950,
              fontSize: 18,
              color: AppColors.textPrimary,
            }}
          >
            {formatMoney(
              Number(booking.total_price || 0),
              booking.currency || currency,
            )}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const StatCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        boxShadow: AppColors.shadowCard,
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          "&:last-child": {
            pb: 3,
          },
        }}
      >
        <Typography
          sx={{
            color: AppColors.textSecondary,
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 950,
            mt: 1.1,
            color: AppColors.textPrimary,
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const FilterChip = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => {
  return (
    <Chip
      clickable
      onClick={onClick}
      label={label}
      sx={{
        height: 34,
        borderRadius: "999px",
        fontWeight: 900,
        color: active ? AppColors.textOnPrimary : AppColors.textSecondary,
        bgcolor: active ? AppColors.primary : AppColors.surface,
        border: `1px solid ${active ? AppColors.primary : AppColors.border}`,
        "&:hover": {
          bgcolor: active ? AppColors.primary : AppColors.surfaceSoft,
        },
      }}
    />
  );
};

const SourceChip = ({ source }: { source: BookingSource }) => {
  const { t } = useTranslation();
  const styles = getSourceChipStyles(source);

  return (
    <Chip
      size="small"
      label={t(`bookingSources.${source}`)}
      sx={{
        height: 26,
        fontWeight: 900,
        bgcolor: styles.background,
        color: styles.color,
        border: `1px solid ${styles.border}`,
      }}
    />
  );
};

const PaymentChip = ({
  paymentStatus,
}: {
  paymentStatus: BookingResponse["payment_status"];
}) => {
  const { t } = useTranslation();
  const styles = getPaymentChipStyles(paymentStatus);

  return (
    <Chip
      size="small"
      label={t(`paymentStatus.${paymentStatus}`)}
      sx={{
        height: 26,
        fontWeight: 900,
        bgcolor: styles.background,
        color: styles.color,
        border: `1px solid ${styles.border}`,
      }}
    />
  );
};

const getUnitAccent = (index: number) => {
  const colors = [
    "#14b8a6",
    "#8b5cf6",
    "#22c55e",
    "#f97316",
    "#2563eb",
    "#ec4899",
  ];

  return colors[index % colors.length];
};

const getSourceChipStyles = (source: BookingSource) => {
  if (source === "airbnb") {
    return {
      background: "#fff1f2",
      color: "#e11d48",
      border: "#fecdd3",
    };
  }

  if (source === "booking") {
    return {
      background: "#eff6ff",
      color: "#2563eb",
      border: "#bfdbfe",
    };
  }

  if (source === "external") {
    return {
      background: "#fff7ed",
      color: "#ea580c",
      border: "#fed7aa",
    };
  }

  if (source === "other") {
    return {
      background: "#f5f3ff",
      color: "#7c3aed",
      border: "#ddd6fe",
    };
  }

  return {
    background: "#f8fafc",
    color: "#475569",
    border: "#e2e8f0",
  };
};

const getPaymentChipStyles = (
  paymentStatus: BookingResponse["payment_status"],
) => {
  if (paymentStatus === "paid") {
    return {
      background: "#dcfce7",
      color: "#15803d",
      border: "#bbf7d0",
    };
  }

  if (paymentStatus === "deposit") {
    return {
      background: "#fef3c7",
      color: "#b45309",
      border: "#fde68a",
    };
  }

  return {
    background: "#f8fafc",
    color: "#475569",
    border: "#e2e8f0",
  };
};

export default ReservationsScreen;
